// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@steiner/App/Components/Common/FancyScroll";
import MiniCartLineCard from "@steiner/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@steiner/App/Components/Notify";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { FC, useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    return {
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        cart,
        isLoading: state.data.carts.isLoading,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        rememberMe: getSession(state).rememberMe,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    loadingOverlay?: LoadingOverlayProps;
    cartHeaderWrapper?: InjectableCss;
    subTotalWrapper?: InjectableCss;
    subTotalTitle?: TypographyProps;
    subTotalValue?: TypographyProps;
    totalCountWrapper?: InjectableCss;
    totalCountTitle?: TypographyProps;
    totalCountValue?: TypographyProps;
    cartFooterWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    cartLinesWrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    errorMessagesContainer?: InjectableCss;
    errorMessageNotification?: TypographyProps;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    isLoading,
    isPopupShown,
    isUpdatingCartLine,
    isRemovingCartLine,
}) => {
    const history = useHistory();
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    const cartLines = cart?.cartLines ?? [];

    const isWidgetNonStockItem = useMemo(() => {
        return cart?.cartLines?.some(cartLine => parseBoolean(cartLine.properties?.isWidgetNonStockItem));
    }, [cart]);

    const errorMessages = useMemo(() => {
        const messages = [];

        if (isWidgetNonStockItem) {
            messages.push(siteMessage("STR_Custom_Mini_Cart_NonStockItem_Warning_Message"));
        }

        return messages;
    }, [cart]);

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
    };

    if (!cart) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner size={20} />
            </StyledWrapper>
        );
    }

    return (
        <>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isLoading[cart.id] || isUpdatingCartLine || isRemovingCartLine}
            >
                <StyledWrapper {...styles.cartHeaderWrapper} data-test-selector="miniCart-header-container">
                    <StyledWrapper {...styles.subTotalWrapper}>
                        <Typography {...styles.subTotalTitle}>{translate("Subtotal")}:</Typography>
                        <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.totalCountWrapper}>
                        <Typography {...styles.totalCountTitle}>{translate("Items")}:</Typography>
                        <Typography {...styles.totalCountValue}>{totalCountDisplay}</Typography>
                    </StyledWrapper>
                </StyledWrapper>

                <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                    <StyledWrapper
                        {...styles.cartLinesWrapper}
                        ref={parentBoxRef}
                        data-test-selector="miniCart-cartLines-container"
                    >
                        {!!errorMessages.length && (
                            <StyledWrapper {...styles.errorMessagesContainer}>
                                {errorMessages.map(message => (
                                    <Notify
                                        key={`warning-${message}`}
                                        type="warning"
                                        {...styles.errorMessageNotification}
                                    >
                                        {message}
                                    </Notify>
                                ))}
                            </StyledWrapper>
                        )}
                        {cartLines.length > 0 ? (
                            cartLines?.map(cartLine => (
                                <MiniCartLineCard
                                    key={`miniCartLine_${cartLine.id}`}
                                    cartLine={cartLine}
                                    cart={cart}
                                    qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                    removeCartLineClickHandler={removeCartLineClickHandler}
                                />
                            ))
                        ) : (
                            <StyledWrapper {...styles.notifyWrapper}>
                                <Notify {...styles.noCartLinesNotify} type="info">
                                    {siteMessage("STR_Cart_Empty")}
                                </Notify>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </FancyScroll>

                <StyledWrapper {...styles.cartFooterWrapper} data-test-selector="miniCart-footer-container">
                    <Button {...styles.viewCartButton} onClick={handleViewCarClick} disabled={!cartLines.length}>
                        {translate("View Cart")}
                    </Button>

                    {createWidgetElement("Cart/CartActions", {
                        fields: {
                            isMiniCart: true,
                        },
                    })}
                </StyledWrapper>
            </LoadingOverlay>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
